import React, { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { L2_ACCENT, INK, MUTED } from './colorTokens';

// Shared "All" + multi-checkbox dropdown filter, used across
// OperationalExcellenceHub and LoggingInventoryPage so every filter of this
// shape behaves identically (Status/Priority/Year/Month/Level/PIIRisk, etc.).
export const ALL_FILTER = 'all';

export const isAllFilter = (selected) => Array.isArray(selected) && selected.includes(ALL_FILTER);

export const filterAllows = (selected, ticketValue) => {
  if (!Array.isArray(selected) || selected.length === 0 || isAllFilter(selected)) return true;
  return selected.map(String).includes(String(ticketValue));
};

export const MultiCheckSelect = ({
  label,
  value,
  onChange,
  options,
  sx,
  minWidth = 140,
  listLabels = false
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerInputRef = useRef(null);
  const listboxRef = useRef(null);
  const selected = Array.isArray(value) && value.length ? value : [ALL_FILTER];
  const allChecked = isAllFilter(selected);
  const display = allChecked
    ? 'All'
    : selected.length === 0
      ? 'All'
      : (() => {
        const labels = (options || [])
          .filter((option) => selected.includes(option.value))
          .map((option) => option.label);
        if (labels.length === 1) return labels[0];
        if (labels.length > 1) return listLabels ? labels.join(', ') : `${labels.length} selected`;
        return 'All';
      })();

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Move focus into the listbox when it opens (standard listbox behavior,
  // and required for keyboard users -- previously this dropdown had no
  // keyboard path at all: no way to reach or select an option once open,
  // and no Escape-to-close, despite every other interactive control in this
  // file implementing the equivalent pattern).
  useEffect(() => {
    if (!open || !listboxRef.current) return;
    const first = listboxRef.current.querySelector('[role="option"]');
    if (first) first.focus();
  }, [open]);

  const closeAndRefocusTrigger = () => {
    setOpen(false);
    if (triggerInputRef.current) triggerInputRef.current.focus();
  };

  const toggleAll = () => onChange([ALL_FILTER]);
  const toggleOption = (optionValue) => {
    if (allChecked) {
      onChange([optionValue]);
      return;
    }
    const next = selected.includes(optionValue)
      ? selected.filter((item) => item !== optionValue)
      : [...selected, optionValue];
    onChange(next.length ? next : [ALL_FILTER]);
  };

  const optionRow = (key, checked, text, onPick) => (
    <Box
      key={key}
      role="option"
      tabIndex={0}
      aria-selected={checked}
      onClick={onPick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPick();
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          const next = event.currentTarget.nextElementSibling;
          if (next) next.focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const prev = event.currentTarget.previousElementSibling;
          if (prev) prev.focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeAndRefocusTrigger();
        } else if (event.key === 'Tab') {
          setOpen(false);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        cursor: 'pointer',
        bgcolor: checked && key === ALL_FILTER ? L2_ACCENT : 'transparent',
        color: checked && key === ALL_FILTER ? '#fff' : INK,
        '&:hover': {
          bgcolor: checked && key === ALL_FILTER ? L2_ACCENT : 'rgba(0,0,0,0.06)'
        },
        '&:focus-visible': {
          outline: `2px solid ${L2_ACCENT}`,
          outlineOffset: '-2px'
        }
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        tabIndex={-1}
        sx={{
          p: 0.25,
          pointerEvents: 'none',
          color: checked && key === ALL_FILTER ? '#fff' : undefined,
          '&.Mui-checked': { color: checked && key === ALL_FILTER ? '#fff' : L2_ACCENT }
        }}
      />
      <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
        {text}
      </Typography>
    </Box>
  );

  return (
    <Box ref={rootRef} sx={{ ...sx, minWidth, position: 'relative', zIndex: open ? 3 : 1 }}>
      {/* aria-haspopup/aria-expanded must live on the input itself (role=combobox target), not the InputBase root div */}
      <TextField
        size="small"
        fullWidth
        label={label}
        value={display}
        onClick={() => setOpen((isOpen) => !isOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            setOpen(false);
          }
        }}
        inputRef={triggerInputRef}
        inputProps={{
          role: 'combobox',
          'aria-haspopup': 'listbox',
          'aria-expanded': open
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: -0.5 }}>
              <ArrowDropDownIcon sx={{ color: MUTED, pointerEvents: 'none' }} />
            </InputAdornment>
          )
        }}
        sx={{ '& .MuiInputBase-input': { cursor: 'pointer' } }}
      />
      {open && (
        <Box
          ref={listboxRef}
          role="listbox"
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 20,
            mt: '2px',
            minWidth: '100%',
            maxHeight: 320,
            overflowY: 'auto',
            bgcolor: '#fff',
            border: '1px solid rgba(0,0,0,0.23)',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.16)'
          }}
        >
          {optionRow(ALL_FILTER, allChecked, 'All', toggleAll)}
          {(options || []).map((option) => optionRow(
            option.value,
            !allChecked && selected.includes(option.value),
            option.label,
            () => toggleOption(option.value)
          ))}
        </Box>
      )}
    </Box>
  );
};
